import React, { useState, useEffect, useRef, useMemo } from 'react';
import './BookTrail.css';
import axios from "axios";
import Swal from 'sweetalert2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '@lottiefiles/lottie-player';
import image from './image.png';
import { Clock, Phone, GraduationCap, Waves, Trophy, CheckCircle2, ChevronRight, ChevronLeft, Plus, X } from 'lucide-react';
import Autocomplete from 'react-google-autocomplete';

const termsData = {
  title: "Terms & Conditions",
  sections: [
    {
      heading: "Booking Policy",
      content: "This trial booking is subject to slot availability and confirmation."
    },
    {
      heading: "Payment",
      content: "Any applicable fee paid for the trial may be adjusted against a coaching plan purchase."
    },
    {
      heading: "Reschedule / Cancellation",
      content: "Reschedule requests may be limited based on policy and slot availability."
    }
  ]
};



const BookTrial = () => {
  const [selectedType, setSelectedType] = useState('');
  const [selectedSport, setSelectedSport] = useState('');
  const [userName, setUserName] = useState('');
  const [isCreatingNewChild, setIsCreatingNewChild] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [showCreateChildInput, setShowCreateChildInput] = useState(false);
  const [newChildName, setNewChildName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [selectedCenter, setSelectedCenter] = useState('');
  const [showChildDropdown, setShowChildDropdown] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [formData, setFormData] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [centers, setCenters] = useState([]);
  const [selectedTrialDate, setSelectedTrialDate] = useState('');
  const [selectedTrialTime, setSelectedTrialTime] = useState('');
  const [parentUserId, setParentUserId] = useState(null);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [parentId, setParentId] = useState(null);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [userCategory, setUserCategory] = useState('');
  const [proficiencyLevel, setProficiencyLevel] = useState('');
  const [selectedCourtId, setSelectedCourtId] = useState(null);
  const childDropdownRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const sportConfig = {
    badminton: { id: 1, name: 'Badminton' },
    swimming: { id: 11, name: 'Swimming' }
  };
  const [searchValue, setSearchValue] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [userCoordinates, setUserCoordinates] = useState(null);
  const [slotAmount, setSlotAmount] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(true);

  const trialPolicies = {
    coaching: {
      title: "Coaching Trial Policy",
      policies: [
        ...(selectedSport === "swimming"
          ? [
            "For swimming coaching trials, participants below 14 years must be accompanied by a parent or guardian who remains in the designated pool area throughout the session."
          ]
          : []),
        "All coaching trials are chargeable. The applicable fee will be shown at time of booking.",
        "The trial fee will be adjusted against purchase of a coaching plan for same sport by same user (applicable once per user per sport).",
        "Trial booking is confirmed only after successful payment.",
        "Trial fee is non-refundable in case of cancellation or no-show.",
        "One-time rescheduling is permitted, subject to slot availability.",
        "Trial is valid only for selected centre, date and time.",
        "Users must follow all safety, hygiene, and facility guidelines."
      ]
    },
    playing: {
      title: "Machaxi Swimming Playing Trial - Terms & Conditions",
      policies: [
        "Swimming playing trials are chargeable. The applicable fee will be shown at time of booking.",
        "The trial fee will be adjusted when user purchases a Swimming Playing Membership (applicable once per user).",
        "Each individual is entitled to only one trial per sport. Duplicate bookings using multiple phone numbers or accounts may be declined.",
        "Trial slot is confirmed only after successful payment.",
        "Trial fee is non-refundable, including in case of cancellation or no-show.",
        "Trial is valid only for selected date and time slot.",
        "A one-time rescheduling of your trial is permitted up to 3 hours before the scheduled start time, subject to slot availability.",
        "For swimming trials, a swimming costume and swimming cap are mandatory. The centre may deny participation if the required swimwear is not worn or carried.",
        "All instructions from centre staff must be followed during session.",
        "Trial access does not guarantee future slot availability under membership plans."
      ]
    }
  };


  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  };

  const sortCentersByDistance = (lat, lng, centersList = centers) => {
    return centersList
      .map(center => ({
        ...center,
        distance: calculateDistance(
          lat,
          lng,
          Number(center.latitude),
          Number(center.longitude)
        )
      }))
      .sort((a, b) => a.distance - b.distance);
  };

  const getLocationName = async (latitude, longitude) => {
    try {
      const response = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
      );

      if (response.data.results && response.data.results.length > 0) {
        setSearchValue(response.data.results[0].formatted_address);
      }

      setUserCoordinates({ lat: latitude, lng: longitude });
      setCenters(sortCentersByDistance(latitude, longitude));
    } catch (err) {
      console.error("Geocoding error:", err);
      Swal.fire('Error', 'Failed to fetch your address.', 'error');
    }
  };

  const updateLocation = async () => {
    if (!navigator.geolocation) {
      Swal.fire('Error', 'Geolocation is not supported.', 'error');
      return;
    }

    setLocationLoading(true);
    setUserCoordinates(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        getLocationName(latitude, longitude);
        setLocationLoading(false);
      },
      (error) => {
        setLocationLoading(false);

        let msg = 'Location access denied.';
        if (error.code === 1) msg = 'Please enable location permissions in your Browser and System Settings.';
        if (error.code === 2) msg = 'Location unavailable. Try searching manually.';
        if (error.code === 3) msg = 'Location request timed out.';

        Swal.fire('Location Error', msg, 'error');
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  };

  const handleLocationSearch = (place) => {
    if (place.geometry) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      setSearchValue(place.formatted_address);
      setUserCoordinates({ lat, lng });
      setCenters(sortCentersByDistance(lat, lng));
    }
  };

  const getApiEndpoints = () => {
    if (selectedType === 'coaching') {
      return {
        trial: process.env.REACT_APP_API_URL_COACHING_TRIAL,
        slotCheck: process.env.REACT_APP_API_URL_SLOT_CHECK,
        validateSlots: process.env.REACT_APP_API_URL_VALIDATE_COACHING_SLOTS,
      };
    } else {
      return {
        trial: process.env.REACT_APP_API_URL_PLAY_TRIAL,
        slotCheck: process.env.REACT_APP_API_URL_SLOT_CHECK_PLAYING,
        validateSlots: process.env.REACT_APP_API_URL_VALIDATE_PLAYING_SLOTS,
      };
    }
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setSelectedSport('');
    resetForm();
  };

  const handleSportSelect = (sport) => {
    setSelectedSport(sport);
    setSelectedCenter('');
    setSelectedTrialDate('');
    setSelectedTrialTime('');
    setAvailableSlots([]);
    setSearchValue('');
    setUserCoordinates(null);
  };

  const resetForm = () => {
    setSelectedCenter('');
    setSelectedTrialDate('');
    setSelectedTrialTime('');
    setAvailableSlots([]);
    setUserCategory('');
    setProficiencyLevel('');
    setSelectedCourtId(null);
    setSearchValue('');
    setUserCoordinates(null);
  };

  const fetchSportsCenters = async () => {
    if (!selectedSport) return;

    try {
      const response = await fetch(process.env.REACT_APP_API_URL_CENTER, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY,
        }
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch sports centers.');
      }

      const defaultImages = [
        "https://imgmediagumlet.lbb.in/media/2022/01/61efa39514a08c2400a7ce56_1643094933277.png",
        "https://i.ytimg.com/vi/K8RGq4xtJbg/hq720.jpg?sqp=-oaymwEhCK4FEIIDSFryq4qpAxMIARUAAAAAGAElAADIQj0AgKJD&rs=AOn4CLAnvqqpSN_Z3Gha4COeOk9un1cj8Q",
        "https://www.scai.in/wp-content/uploads/2023/06/DSC2954-scaled.jpg"
      ];

      const sportKey = selectedSport === 'badminton' ? 'Badminton' : 'Swimming';
      const centersData = Object.values(data.sportsToAcademiesMap[sportKey]).flat();
      const academyImages = data.academyImages || {};
      const excludeIds = selectedSport === 'badminton' ? [81, 115, 86] : [70, 78, 81, 86, 115];
      setCenters(
        centersData.filter(center => {
          const academyId = center?.id;
          return !excludeIds.includes(academyId);
        }).map(center => {
          const academyId = center?.id;
          const sports = data.academiesToSportsMap[academyId] || [];
          return {
            id: center.id,
            name: center.name,
            latitude: Number(center.latitude) || 0,
            longitude: Number(center.longitude) || 0,
            address: center.address || '',
            mapUrl:
              center.location ||
              center.map_url ||
              `https://www.google.com/maps/search/?api=1&query=${center.latitude},${center.longitude}`,
            sports: sports.map(sport => sport.sport_name) || [],
            images: academyImages[academyId] || defaultImages,
          }
        })
      );
    } catch (error) {
      console.error('Error fetching data:', error);
      showBackendError(error);
    }
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return selectedType && selectedSport && selectedCenter;
      case 2:
        if (selectedType === "playing" && selectedSport === "swimming") {
          return true;
        }
        return userCategory && proficiencyLevel;

      case 3:
        return selectedTrialDate && selectedTrialTime;

      case 4:
        return contactNumber && isOtpVerified;

      default:
        return false;
    }
  };

  useEffect(() => {
    if (selectedSport && centers.length > 0 && !userCoordinates) {
      updateLocation();
    }
  }, [selectedSport, centers.length]);

  useEffect(() => {
    if (selectedSport) {
      fetchSportsCenters();
    }
  }, [selectedSport]);

  useEffect(() => {
    if (contactNumber) {
      setIsOtpVerified(false);
      setOtp('');
      setUserName('');
      setUserDetails(null);
      setShowOtpInput(false);
      setIsNewUser(false);
      setIsOtpSent(false);
      setIsOtpLoading(false);
    }
  }, [contactNumber]);


  useEffect(() => {
    setSelectedTrialDate('');
    setSelectedTrialTime('');
    setAvailableSlots([]);
  }, [userCategory, proficiencyLevel]);

  useEffect(() => {
    setSelectedTrialTime(null);
  }, [selectedTrialDate]);

  useEffect(() => {
    if (selectedType === 'playing' && selectedSport === 'swimming') {
      setProficiencyLevel('BASIC');
    }
  }, [selectedType, selectedSport]);


  const handleCenterChange = (centerName) => {
    setSelectedCenter(centerName);
    setSelectedTrialDate('');
    setSelectedTrialTime('');
    setAvailableSlots([]);
  };

  const handleCategoryChange = (category) => {
    setUserCategory(category);
  };

  const handleProficiencyChange = (level) => {
    setProficiencyLevel(level);;
  };

  const getGridTemplateColumns = () => {
    const numSteps = selectedType === 'coaching' ? 4 : 3;
    const columns = [];
    for (let i = 0; i < numSteps; i++) {
      columns.push('40px');
      if (i < numSteps - 1) {
        columns.push('1fr');
      }
    }
    return columns.join(' ');
  };

  const fetchUserDetails = async (mobileNumber) => {
    try {
      const formattedMobileNumber = mobileNumber.startsWith('+91')
        ? mobileNumber
        : '+91' + mobileNumber;

      const response = await fetch(process.env.REACT_APP_API_URL_FETCH_USER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({ mobileNumber: formattedMobileNumber }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch user details.');
      }

      if (data && data.userDetails.success) {
        const validChildren = (data.userDetails.children || []).filter(
          (child) => child?.id && child?.name
        );

        const cleanedUserDetails = {
          ...data.userDetails,
          children: validChildren,
        };

        const isNewUser = cleanedUserDetails.parent.name === null;
        setUserDetails(cleanedUserDetails);
        setIsNewUser(isNewUser);
        if (!isNewUser)
          setParentId(data.userDetails.parent.id);
        setSelectedChildId(null);
      } else {
        setUserDetails(null);
        setIsNewUser(true);
        setParentId(null);
        setSelectedChildId(null);
      }
    } catch (error) {
      setIsNewUser(true);
      setParentId(null);
      setSelectedChildId(null);
      console.error('Error fetching user details:', error);
    }
  };


  const handleChildSelect = (child) => {
    if (child.isParent) {
      setSelectedChildId('parent');
      setParentUserId(userDetails.parent.id);
      setUserName(child.name);
    } else {
      setSelectedChildId(child.id);
      setParentUserId(null);
      setUserName(child.name);
    }
    setIsCreatingNewChild(false);
    setShowChildDropdown(false);
  };

  const handleSendOtpClick = async () => {
    const formattedContactNumber = contactNumber.startsWith('+91') ? contactNumber : `+91${contactNumber}`;

    try {
      setIsOtpLoading(true);
      const response = await axios.post(process.env.REACT_APP_API_URL_OTP_SEND, {
        data: {
          phone_number: formattedContactNumber,
          has_firebase_check: false,
        },
      });

      if (response.data.success) {
        setShowOtpInput(true);
        setIsOtpVerified(false);
        setOtp('');
        setIsOtpSent(true);
        Swal.fire({
          toast: true, position: 'top-end', icon: 'success',
          title: `OTP sent to ${formattedContactNumber} via WhatsApp and SMS. Please enter it to proceed.`,
          showConfirmButton: false, timer: 3000, timerProgressBar: true,
        });
        await fetchUserDetails(contactNumber);
      } else {
        Swal.fire({ icon: 'error', title: 'Failed to Send OTP', text: response.data.message || 'Something went wrong.' });
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to send OTP. Please try again.' });
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleOtpVerification = async () => {
    try {
      const formattedContactNumber = contactNumber.startsWith('+91') ? contactNumber : `+91${contactNumber}`;
      const response = await axios.post(process.env.REACT_APP_API_URL_OTP_VERIFY, {
        data: {
          phone_number: formattedContactNumber,
          has_firebase_check: false,
          otp: otp,
        },
      });

      if (response.data.success) {
        setIsOtpVerified(true);
      } else {
        Swal.fire({ icon: 'error', title: 'Invalid OTP', text: response.data.message || 'The OTP you entered is invalid.' });
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to verify OTP. Please try again.' });
    }
  };

  const handleInputChange = (e, field) => {
    const value = e.target.value;

    switch (field) {
      case 'userName': setUserName(value); break;
      case 'contactNumber': setContactNumber(value); break;
      case 'otp': setOtp(value); break;
      default: break;
    }
  };

  const handleDateChange = (date) => {
    setSelectedTrialDate(date);
  };

  const handleTrialTimeChange = (time, batchId, status, courtId = null) => {
    if (status === 'Available') {
      setSelectedTrialTime(time);
      setSelectedBatchId(batchId);
      if (courtId) setSelectedCourtId(courtId);
    }
  };
  const formatDateForSwimming = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchAvailableSlots = async () => {
    try {
      const selectedCenterData = centers.find(center => center.name === selectedCenter);
      if (!selectedCenterData) throw new Error('Selected center not found.');

      const formattedDate = formatDateToYYYYMMDD(selectedTrialDate);
      const sportId = sportConfig[selectedSport].id;
      const mapProficiency = (level) => {
        if (!level) return '';
        if (level.toLowerCase() === "beginner") return "BASIC";
        return level.toUpperCase();
      };

      let payload = {};
      if (selectedType === 'coaching') {
        if (selectedSport === "swimming") {
          payload = {
            academy_id: selectedCenterData.id,
            batch_type: userCategory === 'Adult' ? "ADULTS" : "KIDS",
            booking_date: formatDateForSwimming(selectedTrialDate),
            isCoaching: 1,
            isPlaying: 0,
            proficiency_type: mapProficiency(proficiencyLevel),
            sport_id: sportId,
            // sport_id: sportId === 1 ? 1 : 11,
            user_id: newChildName ? null : (parentId || selectedChildId || null),// Send null for new child, otherwise send user_id
          };
        } else {
          payload = {
            isPlaying: 0,
            isCoaching: 1,
            academy_id: selectedCenterData.id,
            sport_id: sportId,
            // sport_id: sportId === 1 ? 1 : 11,
            batch_type: userCategory === 'Adult' ? "ADULTS" : "KIDS",
            proficiency_type: mapProficiency(proficiencyLevel),
            booking_date: formattedDate,
            user_id: newChildName ? null : (parentId || selectedChildId || null),// Send null for new child, otherwise send user_id
          };
        }
      } else {
        payload = {
          venueId: selectedCenterData.id,
          sportId: sportId,
          // sportId: 1,
          date: formattedDate.replace(/:/g, '-'),
          user_id: parentId,
        };
      }

      const apiEndpoints = getApiEndpoints();
      const response = await axios.post(apiEndpoints.slotCheck, payload, {
        headers: { 'Content-Type': 'application/json', 'x-api-key': process.env.REACT_APP_API_KEY }
      });

      if (selectedType === 'coaching') {
        if (response.data.message === 'Available Playing slots fetched successfully !') {
          setAvailableSlots(response.data.availableCoachingSlots);
          setSlotAmount(response.data.amount);
          const isReschedule = response.data?.is_reschedule;
          return { isReschedule };
        } else if (response.data.message === 'Rescheduling limit exceeded' && !response.data.success) {
          // Handle rescheduling limit exceeded for coaching
          setAvailableSlots([]);
          setSlotAmount(null);
          Swal.fire({
            icon: 'warning',
            title: 'Rescheduling Limit Exceeded',
            text: 'You have reached the maximum number of rescheduling attempts. Please contact support for assistance.',
            confirmButtonColor: '#322978'
          });
          return null;
        } else {
          throw new Error('Failed to fetch available slots.');
        }
      } else {
        // Playing response handling
        if (response.data.message === 'Successfully sent to external API') {
          const courtSlots = response.data.externalResponse.courts || [];
          const processedSlots = courtSlots.flatMap(court =>
            court.slots.map(slot => ({
              court_id: court.courtId,
              open_time: slot.startTime,
              close_time: slot.endTime,
              status: slot.available ? 'Available' : 'Booked',
              available: slot.available,
              ticketsAvailable: slot.ticketsAvailable || 0
            }))
          );
          setAvailableSlots(processedSlots);
          setSlotAmount(response.data.adultTrialAmount);
          const isReschedule = response.data?.is_reschedule;
          return { isReschedule };
        } else if (response.data.message === 'Rescheduling limit exceeded' && !response.data.success) {
          // Handle rescheduling limit exceeded
          setAvailableSlots([]);
          setSlotAmount(null);
          Swal.fire({
            icon: 'warning',
            title: 'Rescheduling Limit Exceeded',
            text: 'You have reached the maximum number of rescheduling attempts. Please contact support for assistance.',
            confirmButtonColor: '#322978'
          });
          return null;
        } else {
          throw new Error('Failed to fetch available slots.');
        }
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
      const rescheduleLimitError = typeof (error?.response?.data?.message) === "string" && error?.response?.data?.message === "Rescheduling limit exceeded";
      if (rescheduleLimitError) {
        setAvailableSlots([]);
        setSlotAmount(null);
        Swal.fire({
          icon: 'warning',
          title: 'Rescheduling Limit Exceeded',
          text: 'You have reached the maximum number of rescheduling attempts. Please contact support for assistance.',
          confirmButtonColor: '#322978'
        });
        const isRescheduleLimitError = true;
        return {
          isRescheduleLimitError
        };
      }
      showBackendError(error);
      Swal.fire({ icon: 'error', title: 'Error', text: error.message || 'Failed to fetch available slots.' });
    }
  };

  useEffect(() => {
    if (selectedTrialDate && selectedCenter) {
      fetchAvailableSlots();
    }
  }, [selectedTrialDate, selectedCenter]);

  const formatDate = (date) => {
    const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
    const formattedDate = new Date(date).toLocaleDateString('en-US', options);
    return formattedDate.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$02-$01-$03');
  };

  const formatDateToYYYYMMDD = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTo12Hour = (time) => {
    if (!time) return '';

    const [hour, minute] = time.split(':');
    let h = parseInt(hour, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';

    h = h % 12;
    h = h ? h : 12; // 0 becomes 12

    return `${h}:${minute} ${ampm}`;
  };

  const formatLabel = (text) => {
    if (!text) return '';
    if (text.toLowerCase() === 'basic') return 'Beginner';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentStep !== totalSteps) return;

    let errors = {};

    if (selectedType === 'coaching' && (!userName || !userName.trim())) {
      errors.userName = 'Name is required';
    }

    if (!contactNumber.trim() || !isContactNumberValid) {
      errors.contactNumber = 'Please enter a valid contact number';
    }
    if (!selectedCenter) {
      errors.selectedCenter = 'Please select a center';
    }
    if (!selectedTrialDate) {
      errors.selectedTrialDate = 'Please select a date';
    }
    if (!selectedTrialTime) {
      errors.selectedTrialTime = 'Please select a trial time';
    }
    if (!isOtpVerified) {
      errors.otp = 'Please verify OTP before submitting';
    }
    if (selectedType === 'coaching' && !userCategory.trim()) {
      errors.userCategory = 'Please select a Category';
    }
    if (!proficiencyLevel.trim()) {
      errors.proficiencyLevel = 'Please select a Proficiency Level';
    }
    if (!selectedType) {
      errors.selectedType = 'Please select a type';
    }
    if (!selectedSport) {
      errors.selectedSport = 'Please select a sport';
    }
    if (!isTermsAccepted) {
      errors.termsAccepted = 'Please accept the Terms & Conditions';
    }

    const firstError = Object.values(errors).find((err) => err !== '');
    if (firstError) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'error',
        title: firstError,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }
    const slotsData = await fetchAvailableSlots();
    console.log("DEBUG - slotsData", slotsData);
    const isReschedule = slotsData?.isReschedule || false;
    const isRescheduleLimitError = slotsData?.isRescheduleLimitError || false;
    if (isRescheduleLimitError) return;

    const bookingData = {
      userName,
      contactNumber,
      selectedCenter,
      selectedTrialDate,
      selectedTrialTime,
      selectedChildId,
      amount: isReschedule ? 0 : slotAmount,
      isReschedule
    };

    setFormData(bookingData);

    setShowConfirmationModal(true);
  };

  const handleDirectBookingForZeroAmount = async (bookingData, parentIdToSend, childIdToSend, academyIdToSend, formattedMobileNumber) => {
    try {
      const selectedCenterData = centers.find(center => center.name === bookingData.selectedCenter);
      const [startTime, endTime] = bookingData.selectedTrialTime.split(" - ");

      let directBookingPayload = {};

      if (selectedType === 'playing') {
        // Playing payload structure
        directBookingPayload = {
          isPlayingTrial: 1,
          isCoachingTrial: 0,
          user_id: childIdToSend || parentIdToSend,
          name: bookingData.userName,
          email: null,
          mobile_number: formattedMobileNumber,
          gender_type: "male", // Default to female as per example
          sport_id: 11, // Swimming sport ID as per example
          academy_id_new: academyIdToSend,
          academy_id_prev: academyIdToSend,
          court_id: selectedCourtId,
          start_time: `${startTime}`,
          end_time: `${endTime}`,
          booking_date: formatDateToYYYYMMDD(bookingData.selectedTrialDate),
          area: null,
          source: "Hundred_leads"
        };

        // isPlayingTrial: 1,
        //   isCoachingTrial: 0,
        //   user_id: childIdToSend || parentIdToSend,
        //   name: bookingData.userName,
        //   email: null,
        //   mobile_number: formattedMobileNumber,
        //   gender_type: "female", // Default to female, can be made dynamic
        //   sport_id: 11,
        //   academy_id_new: academyIdToSend,
        //   academy_id_prev: academyIdToSend,
        //   court_id: selectedCourtId,
        //   start_time: bookingData.selectedTrialTime.split(" - ")[0],
        //   end_time: bookingData.selectedTrialTime.split(" - ")[1],
        //   booking_date: formatDateToYYYYMMDD(bookingData.selectedTrialDate),
        //   area: null,
        //   source: "Swimming_Playing"
      } else {
        // Coaching payload structure
        directBookingPayload = {
          isPlayingTrial: 0,
          isCoachingTrial: 1,
          user_id: childIdToSend || parentIdToSend,
          name: bookingData.userName,
          email: null,
          mobile_number: formattedMobileNumber,
          gender_type: "male", // Default to female as per example
          sport_id: sportConfig[selectedSport].id === 1 ? 1 : 11,
          academy_id_new: academyIdToSend,
          academy_id_prev: academyIdToSend,
          start_time: `${startTime}:00`,
          end_time: `${endTime}:00`,
          booking_date: formatDateToYYYYMMDD(bookingData.selectedTrialDate),
          area: null,
          source: "Hundred_leads",
          batch_id: selectedBatchId
        };
      }

      const response = await axios.post(
        'https://admin.machaxi.com/summer-api/trial/reschedule-trial2',
        directBookingPayload,
        {
          headers: {
            'x-api-key': 'jNtunfkUwiAT4YXgQacG84SXO0ZRrzCxSnyUaFunqg',
            'Content-Type': 'application/json'
          }
        }
      );

      const rescheduleSuccess = typeof (response?.data?.message) === "string" && response?.data?.message === "Trial rescheduled Successfully";
      if (rescheduleSuccess) {
        Swal.fire({
          icon: 'success',
          title: 'Booking Confirmed!',
          text: 'Your trial has been rescheduled successfully.',
          confirmButtonColor: '#322978'
        }).then(() => {
          window.location.href = '/SuccessPage';
          sessionStorage.setItem('paymentStatus', 'success');
          sessionStorage.setItem('message', 'Your trial has been successfully booked.');
          setShowConfirmationModal(false);
          setContactNumber('');
          setIsOtpVerified(false);
          setOtp('');
          setShowOtpInput(false);
        });
      } else {
        throw new Error(response.data.message || 'Failed to confirm booking');
      }
    } catch (error) {
      console.error('Error in direct booking:', error);
      Swal.fire({
        icon: 'error',
        title: 'Booking Failed',
        text: error.response?.data?.message || error.message || 'Failed to confirm booking. Please try again.',
        confirmButtonColor: '#322978'
      });
    }
  };

  const handleDirectTrialBooking = async (directBookingPayload) => {
    const response = await axios.post(
      getApiEndpoints().trial,
      directBookingPayload,
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY
        }
      }
    );

    if (response.status < 200 || response.status >= 300 || response?.data?.success === false) {
      throw new Error(response?.data?.message || 'Failed to book trial');
    }

    Swal.fire({
      icon: 'success',
      title: 'Booking Confirmed!',
      text: 'Your trial has been successfully booked.',
      confirmButtonColor: '#322978'
    }).then(() => {
      window.location.href = '/SuccessPage';
      sessionStorage.setItem('paymentStatus', 'success');
      sessionStorage.setItem('message', 'Your trial has been successfully booked.');
      setShowConfirmationModal(false);
      setContactNumber('');
      setIsOtpVerified(false);
      setOtp('');
      setShowOtpInput(false);
    });
  };

  const handleConfirmBooking = async (bookingData) => {
    try {
      let parentIdToSend = null;
      let childIdToSend = null;
      let isChildBooking = userCategory === "Kids";

      if (newChildName) {
        parentIdToSend = userDetails?.parent?.id || null;
        childIdToSend = null;
      } else if (selectedChildId === 'parent') {
        parentIdToSend = userDetails?.parent?.id || null;
        childIdToSend = parentUserId;
      } else if (selectedChildId) {
        parentIdToSend = userDetails?.parent?.id || null;
        childIdToSend = selectedChildId;
      } else {
        parentIdToSend = userDetails?.parent?.id || null;
        childIdToSend = null;
      }

      const selectedCenterData = centers.find(center => center.name === bookingData.selectedCenter); // ← Use bookingData
      const academyIdToSend = selectedCenterData ? selectedCenterData.id : null;
      const formattedMobileNumber = bookingData.contactNumber.startsWith('+91') ? bookingData.contactNumber : '+91' + bookingData.contactNumber; // ← Use bookingData
      const formattedProficiencyLevel = proficiencyLevel.toUpperCase();

      if (bookingData.isReschedule) {
        await handleDirectBookingForZeroAmount(bookingData, parentIdToSend, childIdToSend, academyIdToSend, formattedMobileNumber);
        return;
      }

      let bookingDataForPurchase = {};

      if (selectedType === 'coaching') {
        bookingDataForPurchase = {
          parent_id: parentIdToSend,
          user_id: isChildBooking ? childIdToSend : parentIdToSend,
          name: bookingData.userName, // ← Use bookingData
          mobile_number: formattedMobileNumber,
          gender_type: "Male",
          academy_id: academyIdToSend,
          batch_id: selectedBatchId,
          sport_id: sportConfig[selectedSport].id,
          start_time: bookingData.selectedTrialTime.split(" - ")[0], // ← Use bookingData
          end_time: bookingData.selectedTrialTime.split(" - ")[1], // ← Use bookingData
          booking_date: formatDateToYYYYMMDD(bookingData.selectedTrialDate), // ← Use bookingData
          // city: bookingData.selectedCity,
          area: bookingData.selectedCenter,
          proficiency: formattedProficiencyLevel,
          source: "Hundred_leads",
          payment_id: null,
          amount: bookingData.amount,
        };
      } else {
        // Playing
        const [startTime, endTime] = bookingData.selectedTrialTime.split(" - "); // ← Use bookingData
        bookingDataForPurchase = {
          parent_id: parentIdToSend,
          user_id: childIdToSend || parentIdToSend,
          name: bookingData.userName, // ← Use bookingData
          mobile_number: formattedMobileNumber,
          gender_type: "Male",
          academy_id: academyIdToSend,
          court_id: selectedCourtId,
          sport_id: sportConfig[selectedSport].id,
          start_time: `${startTime}:00`,
          end_time: `${endTime}:00`,
          booking_date: formatDateToYYYYMMDD(bookingData.selectedTrialDate), // ← Use bookingData
          proficiency: formattedProficiencyLevel,
          area: bookingData.selectedCenter,
          source: "Hundred_leads",
          payment_id: null,
          amount: bookingData.amount,
        };
      }

      // Validate Slots
      const validatePayload = {
        parent_id: parentIdToSend,
        user_id: isChildBooking ? childIdToSend : parentIdToSend,
        name: bookingData.userName,
        mobile_number: formattedMobileNumber,
        source: "Hundred_leads",
        academy_id: academyIdToSend,
        batch_id: selectedBatchId,
        // sport_id: 11,
        sport_id: sportConfig[selectedSport].id === 1 ? 1 : 11,
        booking_date: formatDateToYYYYMMDD(bookingData.selectedTrialDate),
        amount: bookingData.amount,
        payment_id: null,
        start_time: bookingData.selectedTrialTime.split(" - ")[0],
        end_time: bookingData.selectedTrialTime.split(" - ")[1],
        area: bookingData.selectedCenter,
        proficiency: proficiencyLevel,
        court_id: selectedCourtId
      }

      const validateSlotsApi = getApiEndpoints().validateSlots;

      const validateResponse = await axios.post(validateSlotsApi, validatePayload, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY
        }
      });

      console.log("DEBUG - Slot validation response:", validateResponse);
      if (validateResponse?.data?.result?.isValid) {
        console.log("DEBUG - Slot validation successful:", validateResponse.data.message);
      } else {
        throw new Error(validateResponse.data.message || 'Selected slot is no longer available. Please choose a different slot.');
      }

      if (!bookingData.isReschedule && bookingData.amount !== null && bookingData.amount !== undefined && Number(bookingData.amount) === 0) {
        await handleDirectTrialBooking(bookingDataForPurchase);
        return;
      }

      //Create Razorpay order
      const razorpayPayload = {
        amount: bookingData.amount * 100,
        currency: 'INR',
        receipt: `receipt_${Date.now()}`,
        ...(selectedType === 'coaching' && { paymentType: 'learn' })
      }

      const razorpayOrder = await axios.post(
        process.env.REACT_APP_API_URL_ORDER_ID,
        razorpayPayload,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_API_KEY,
          }
        }
      );

      if (!razorpayOrder.data || !razorpayOrder.data.id) {
        throw new Error('Failed to create Razorpay order. Please try again.');
      }

      console.log("DEBUG - Razorpay Order Response:", razorpayOrder.data);
      const razorpayOrderId = razorpayOrder.data.id;


      // Create backend order
      const backendOrderPayload = {
        userId: parentIdToSend,
        orderDetails: [{
          academy: academyIdToSend,
          amount: bookingData.amount, // Amount in INR
          amountAfterDiscount: bookingData.amount, // Amount in INR
          discountAmount: 0,
          gstAmount: 0,
          payableAmount: bookingData.amount, // Amount in INR
          paymentId: null,
          regRenFees: null,
          regRenType: null,
          isCoaching: selectedType === 'coaching',
          isRenewal: false,
          isTrial: true,
          rpOrderId: razorpayOrderId,
        }]
      }

      const backendOrderResponse = await axios.post(process.env.REACT_APP_API_URL_CREATE_ORDER,
        backendOrderPayload,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': "jNtunfkUwiAT4YXgQacG84SXO0ZRrzCxSnyUaFunQqg",

          }
        }
      );

      if (!backendOrderResponse.data || !backendOrderResponse.data.data || !backendOrderResponse.data.data[0] || !backendOrderResponse.data.data[0].orderId) {
        throw new Error('Failed to create backend order. Please try again.');
      }

      console.log("DEBUG - Backend Order Response:", backendOrderResponse.data);
      const backendOrderId = backendOrderResponse.data.data?.[0].orderId;

      // Create Pre-order details for razorpay webhook
      const preOrderPayload = {
        parent_id: parentIdToSend,
        user_id: isChildBooking ? childIdToSend : parentIdToSend,
        name: bookingData.userName,
        mobile_number: `+91${bookingData.contactNumber}`,
        gender_type: "Male",
        batch_id: selectedBatchId,
        academy_id: academyIdToSend,
        sport_id: sportConfig[selectedSport].id === 1 ? 1 : 11,
        start_time: bookingData.selectedTrialTime.split(" - ")[0],
        end_time: bookingData.selectedTrialTime.split(" - ")[1],
        booking_date: formatDateToYYYYMMDD(bookingData.selectedTrialDate),
        proficiency: formattedProficiencyLevel,
        area: selectedCenter.name,
        source: "Hundred_leads",
        is_coaching: selectedType === 'coaching' ? 1 : 0,
        discount: 0,
        amount: bookingData.amount,
        final_payable_amount: bookingData.amount,
        coupon_code: null,
        order_id: backendOrderId,
        rz_order_id: razorpayOrderId,
        court_id: selectedCourtId
      }

      console.log("DEBUG - Pre-Order Payload:", preOrderPayload);

      const preOrderResponse = await axios.post(process.env.REACT_APP_API_URL_CREATE_PRE_ORDER,
        preOrderPayload,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_API_KEY,
          }
        }
      );

      console.log("DEBUG - preOrderResponse", preOrderResponse.data);

      const razorpayKey = selectedType === 'coaching'
        ? process.env.REACT_APP_API_COACHING_RAZORPAY_KEY
        : process.env.REACT_APP_API_RAZORPAY_KEY;

      if (!razorpayKey) {
        throw new Error(`Razorpay key is not configured for ${selectedType} trials.`);
      }

      // Open razorpay and take payment
      const razorpay = new window.Razorpay({
        key: razorpayKey,
        amount: razorpayOrder.data.amount,
        currency: razorpayOrder.data.currency,
        order_id: razorpayOrder.data.id,
        name: selectedType === 'playing' ? 'Machaxi Swimming Playing Trial' : selectedSport === 'swimming' ? 'Machaxi Swimming Coaching Trial' : 'Machaxi Badminton Coaching Trial',
        description: `Booking - ${userDetails.parent.name || userName}, MN:${bookingData.contactNumber}`,

        handler: async function (response) {
          console.log('Razorpay payment success response:', response);

          if (
            response.razorpay_payment_id &&
            response.razorpay_order_id &&
            response.razorpay_signature
          ) {
            console.log("DEBUG - Payment successful, verifying order status with backend...");
            const verificationPayload = {
              orderId: razorpayOrderId
            }
            try {
              const verifyResponse = await axios.post(process.env.REACT_APP_API_URL_VERIFY_ORDER_STATUS,
                verificationPayload,
                {
                  headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': 'jNtunfkUwiAT4YXgQacG84SXO0ZRrzCxSnyUaFunQqg',
                  }
                }
              );
              console.log("DEBUG - Verify Payment Response:", verifyResponse.data);
              if (verifyResponse.data.success) {
                window.location.href = '/SuccessPage';
                sessionStorage.setItem('paymentStatus', 'success');
                sessionStorage.setItem('message', 'Your trial has been successfully booked.');
                setShowConfirmationModal(false);
                setContactNumber('');
                setIsOtpVerified(false);
                setOtp('');
                setShowOtpInput(false);
              } else {
                throw new Error(verifyResponse.data.message || 'Payment verification failed');
              }
            } catch (err) {
              console.log('Error inside verifyPayment handler:', err);
              Swal.fire({
                icon: 'error',
                title: 'Payment Verification Failed',
                text: err.message || 'Your payment was successful, but we could not verify it. Please contact support with your payment details.',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true
              });
            }
          }
        },

        modal: {
          ondismiss: function () {

            Swal.fire({
              icon: 'info',
              title: 'Payment Cancelled',
              text: 'If amount is deducted, please contact support.',
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true
            });
          }
        },

        prefill: {
          name: userDetails?.parent?.name || userName,
          contact: bookingData.contactNumber,
        },

        theme: {
          color: '#322978'
        }
      });

      razorpay.on('payment.failed', function (err) {
        console.log('Razorpay payment.failed event:', err);

        Swal.fire({
          icon: 'error',
          title: 'Payment Failed',
          text: 'Please try again. If amount is deducted, contact support.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 4000
        });
      });

      razorpay.open();
    } catch (error) {
      console.error('Error during confirmation:', error);
      let errorMessage = 'Failed to process the booking';
      if (error.response && error.response.data) {
        errorMessage = error.response.data.message || JSON.stringify(error.response.data);
      } else if (error.message) {
        errorMessage = error.message;
      }
      Swal.fire({ icon: 'error', title: 'Error', text: errorMessage });
    }
  };

  const isContactNumberValid = /^[0-9]{10}$/.test(contactNumber);

  const showBackendError = (error) => {
    let errorMessage = 'Something went wrong. Please try again.';
    if (error.response && error.response.data && error.response.data.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: errorMessage
    });
  };

  const visibleCenters = useMemo(() => {
    const uniqueCenters = Array.from(
      new Map(
        centers
          .filter((center) => center && center.id !== 70 && center.id !== 78)
          .map((center) => [
            center.id,
            {
              ...center,
              distance: userCoordinates
                ? calculateDistance(
                  userCoordinates.lat,
                  userCoordinates.lng,
                  Number(center.latitude),
                  Number(center.longitude)
                )
                : null,
              excludeDates: [],
            },
          ])
      ).values()
    );

    return uniqueCenters.sort((a, b) => {
      if (a.distance == null && b.distance == null) return 0;
      if (a.distance == null) return 1;
      if (b.distance == null) return -1;
      return a.distance - b.distance;
    });
  }, [centers, userCoordinates]);

  const parseLocalDate = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const selectedCenterExcludeDates = useMemo(() => {
    const center = visibleCenters.find((item) => item.name === selectedCenter);
    return (center?.excludeDates || []).map(parseLocalDate);
  }, [visibleCenters, selectedCenter]);

  // Terms Modal Component
  const TermsModal = ({ showModal, onClose }) => {
    if (!showModal) return null;

    const currentPolicy = trialPolicies[selectedType] || trialPolicies.coaching;

    return (
      <div className="terms-modal-overlay">
        <div className="terms-modal-content">
          <div className="terms-modal-header">
            <h3>{currentPolicy.title}</h3>
            <button className="terms-close-btn" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div className="terms-modal-body">
            <ol className="terms-list">
              {currentPolicy.policies.map((policy, index) => (
                <li key={index} className="terms-item">
                  {policy}
                </li>
              ))}
            </ol>
          </div>
          <div className="terms-modal-footer">
            <button onClick={onClose} className="terms-accept-btn">
              I Understand
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Confirmation Modal Component
  const ConfirmationModal = ({ showModal, onClose, bookingData }) => {
    if (!showModal) return null;

    const [start, end] = bookingData.selectedTrialTime.split(' - ');

    return (
      <div className="confirmation-modal-overlay">
        <div className="modal-content">
          <button className="close-btn" onClick={onClose}>
            <span className="close-icon">&times;</span>
          </button>

          <h3 className="modal-title">Booking Summary</h3>

          <div className="booking-summary">
            <p><strong>Type:</strong> {selectedType} - {selectedSport}</p>
            <p><strong>Name:</strong> {bookingData.userName}</p>
            <p><strong>Contact:</strong> {bookingData.contactNumber}</p>
            <p><strong>Center:</strong> {bookingData.selectedCenter}</p>
            <p><strong>Date:</strong> {formatDate(bookingData.selectedTrialDate)}</p>
            <p><strong>Time:</strong> {formatTo12Hour(start)}-{formatTo12Hour(end)}</p>

            {selectedType === 'coaching' && (
              <p><strong>Category:</strong> {userCategory}</p>
            )}

            <p><strong>Proficiency:</strong> {formatLabel(proficiencyLevel)}</p>
            {bookingData.isReschedule && (
              <p className="reschedule-note">
                This is a rescheduled booking, so no payment is required.
              </p>
            )}
            {!bookingData.isReschedule && bookingData.amount !== null && bookingData.amount !== undefined && Number(bookingData.amount) === 0 && (
              <p className="reschedule-note">
                No payment is required for this booking.
              </p>
            )}
            <p className="booking-amount-row">
              <strong>Payable Amount:</strong> <span className="booking-amount">₹{bookingData.amount}</span>
            </p>
          </div>
          {!(Number(bookingData.amount === 0) && !bookingData.isReschedule) &&
            (<p className="trial-note">
              {selectedType === "coaching" ? "*This one-time fee will be adjusted against your coaching plan purchase" : "*This one-time fee will be adjusted against your swimming plan purchase"}
            </p>)
          }
          <div className="modal-actions">
            {Number(bookingData.amount) === 0 ? (
              <button
                onClick={() => handleConfirmBooking(bookingData)}
                className="btn-confirm"
              >
                {bookingData.isReschedule ? 'Confirm Reschedule' : 'Confirm Booking'}
              </button>
            ) : (
              <button
                onClick={() => handleConfirmBooking(bookingData)}
                className="btn-confirm"
              >
                {bookingData.amount === 0 ? 'Confirm Reschedule' : `Pay ₹${bookingData.amount}`}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (childDropdownRef.current && !childDropdownRef.current.contains(event.target)) {
        setShowChildDropdown(false);
        setShowCreateChildInput(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  return (
    <div className="trial-ui-wrapper">
      {/* Header */}
      <div className="header-gradient">
        <div className="header-content">
          <img src={image} alt="Logo" className="logo" />
          <h1 className="header-title">Book Your Trial</h1>
          <p className="header-sub">Experience professional coaching and premium facilities</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="progress-wrapper">
        <div
          className="progress-steps"
          style={{ gridTemplateColumns: getGridTemplateColumns() }}
        >
          {Array.from({ length: selectedType === 'coaching' ? totalSteps : totalSteps - 1 }, (_, i) => {
            // Map progress indices to actual step numbers
            let actualStep = i + 1;
            if (selectedType !== 'coaching' && i >= 1) {
              actualStep = i + 2; // Skip step 2 for non-coaching
            }
            return (
              <div
                key={i}
                className="progress-step-container"
                onClick={() => setCurrentStep(actualStep)}
                style={{ cursor: "pointer" }}
              >
                <div
                  className={`progress-step ${currentStep > actualStep ? "done" : currentStep === actualStep ? "booktrial-active" : ""
                    }`}
                >
                  {currentStep > actualStep ? <CheckCircle2 size={18} /> : i + 1}
                </div>
                {i < (selectedType === 'coaching' ? totalSteps - 1 : totalSteps - 2) && (
                  <div className={`progress-line ${currentStep > actualStep ? "done" : ""}`} />
                )}
              </div>
            );
          })}

        </div>
        <div
          className="progress-labels"
          style={{ gridTemplateColumns: selectedType === 'coaching' ? 'repeat(4,1fr)' : 'repeat(3,1fr)' }}
        >
          <span>Type & Sport</span>
          {selectedType === 'coaching' && <span>Preferences</span>}
          <span>Schedule</span>
          <span>Contact</span>
        </div>
      </div>

      {/* Form Card */}
      <div className="form-card">
        <form className="form-body" onSubmit={handleSubmit} noValidate>
          {currentStep === 1 && (
            <>
              {/* Type */}
              <div className="trial-section">
                <h1 className='h1lines'>Select Type, Sport & Location</h1>
                <label className="trial-section-label">Select Type</label>
                <div className="trial-card-options">
                  <button
                    type="button"
                    className={`trial-card ${selectedType === "coaching" ? "active" : ""}`}
                    onClick={() => handleTypeSelect("coaching")}
                  >
                    <GraduationCap className="icon-lg" />
                    <p className="title">Coaching</p>
                    <p className="sub">Learn from expert coaches</p>
                  </button>
                  <button
                    type="button"
                    className={`trial-card ${selectedType === "playing" ? "active" : ""}`}
                    onClick={() => handleTypeSelect("playing")}
                  >
                    <Trophy className="icon-lg" />
                    <p className="title">Playing</p>
                    <p className="sub">Book for fun or practice</p>
                  </button>
                </div>
              </div>

              {/* Sport */}
              {selectedType && (
                <div className="trial-section">
                  <label className="trial-section-label">Select Sport</label>
                  <div className="trial-card-options">
                    {selectedType === "coaching" && (
                      <>
                        <button
                          type="button"
                          className={`trial-card ${selectedSport === "badminton" ? "active" : ""}`}
                          onClick={() => handleSportSelect("badminton")}
                        >
                          🏸
                          <p className="title">Badminton</p>
                        </button>
                        <button
                          type="button"
                          className={`trial-card ${selectedSport === "swimming" ? "active" : ""}`}
                          onClick={() => handleSportSelect("swimming")}
                        >
                          <Waves className="icon-lg" />
                          <p className="title">Swimming</p>
                        </button>
                      </>
                    )}
                    {selectedType === "playing" && (
                      <button
                        type="button"
                        className={`trial-card ${selectedSport === "swimming" ? "active" : ""}`}
                        onClick={() => handleSportSelect("swimming")}
                      >
                        <Waves className="icon-lg" />
                        <p className="title">Swimming</p>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {selectedSport && (
                <div className="trial-section">
                  <label className="trial-section-label">Search Your Location</label>

                  <div className="trial-location-search-row">
                    <Autocomplete
                      apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                      className="trial-input"
                      onPlaceSelected={handleLocationSearch}
                      options={{
                        types: ["geocode", "establishment"],
                        componentRestrictions: { country: "in" },
                      }}
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      placeholder="Search area, street or building..."
                    />

                    <button
                      type="button"
                      className={`trial-btn-secondary ${locationLoading ? "loading" : ""}`}
                      onClick={updateLocation}
                    >
                      <img src="/location.png" alt="Loc" className="geo-icon" />
                    </button>
                  </div>
                </div>
              )}

              {/* Center */}
              {selectedSport && (
                <div className="centers-list-container">
                  <label className="trial-section-label">Select Center</label>

                  <div className="centers-details-row">
                    {locationLoading ? (
                      <p className="no-time-slot">Fetching location...</p>
                    ) :
                      visibleCenters.length > 0 ? visibleCenters.map((center) => (
                        <div
                          key={center.id}
                          className={`mini-center-card ${selectedCenter === center.name ? 'active-card' : ''}`}
                          onClick={() => handleCenterChange(center.name)}
                        >
                          <div className="image-container-mini">
                            <div className="sports-overlay-bar-mini">
                              {(center.sports || []).slice(0, 3).map((sport, idx) => (
                                <div key={idx} className="sport-tag-container">
                                  <span className="sport-name-label-mini">{sport}</span>
                                </div>
                              ))}
                            </div>

                            <img
                              src={(center.images && center.images[0]) || 'https://via.placeholder.com/400x250?text=Center'}
                              alt={center.name}
                              className="center-image-mini"
                            />

                            {center.distance != null && (
                              <div className="distance-badge-mini">
                                {center.distance.toFixed(1)} km away
                              </div>
                            )}
                          </div>

                          <div className="info-container-mini">
                            <div className="text-content">
                              <h3 className="center-name-mini">{center.name}</h3>
                              <p className="center-address-mini">{center.address}</p>

                              <a
                                href={center.mapUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="view-map-link-mini"
                                onClick={(e) => e.stopPropagation()}
                              >
                                View on Map
                              </a>
                            </div>

                            <button type="button" className="select-center-btn-mini">
                              {selectedCenter === center.name ? 'Selected' : 'Select Centre'}
                            </button>
                          </div>
                        </div>
                      )) :
                        <p className="no-time-slot">No centers available</p>
                    }
                  </div>
                </div>
              )}
            </>
          )}

          {/* ---------------- STEP 2 ---------------- */}
          {currentStep === 2 && (
            <>
              <h1 className='h1lines'>Your Preferences</h1>
              {selectedCenter && selectedType === "coaching" && (
                <div className="trial-section">
                  <label className="trial-section-label">Category</label>
                  <div className="trial-card-options small">
                    {["Adult", "Kids"].map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        className={`trial-card ${userCategory === cat ? "active" : ""}`}
                        onClick={() => handleCategoryChange(cat)}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {selectedCenter && !(selectedType === 'playing') && (
                <div className="trial-section">
                  <label className="trial-section-label">Proficiency Level</label>
                  <div className="trial-card-options small">
                    {[
                      { key: "BASIC", label: "Beginner" },
                      { key: "INTERMEDIATE", label: "Intermediate" },
                      { key: "ADVANCED", label: "Advanced" },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        className={`trial-card ${proficiencyLevel === key ? "active" : ""}`}
                        onClick={() => handleProficiencyChange(key)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ---------------- STEP 3 ---------------- */}
          {currentStep === 3 && (
            <>
              <h1 className='h1lines'>Schedule Your Trial</h1>
              {selectedCenter && (
                <div className="trial-section">
                  <label className="trial-section-label">Trial Date</label>
                  <div className="datepicker-wrapper">
                    <DatePicker
                      className="trial-datepicker"
                      selected={selectedTrialDate}
                      onChange={handleDateChange}
                      minDate={new Date()}
                      excludeDates={selectedCenterExcludeDates}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Select trial date"
                      inline

                    />
                  </div>
                </div>
              )}
              {selectedTrialDate && (
                <>
                  {slotAmount !== null && (
                    <div className="trial-section">
                      <div className="trial-fee-banner">
                        <div className="trial-fee-banner-left">
                          <span className="trial-fee-label">Trial Fee</span>
                        </div>
                        <div className="trial-fee-amount">₹{slotAmount}</div>
                      </div>
                    </div>
                  )}
                  <div className="trial-section">
                    <label className="trial-section-label">Available Slots</label>
                    <div className="trial-card-options small">
                      {availableSlots.length > 0 ? (
                        availableSlots.map((slot, index) => {
                          const startTime =
                            selectedType === "coaching"
                              ? slot.start_time?.slice(0, 5)
                              : slot.open_time?.slice(0, 5);
                          const endTime =
                            selectedType === "coaching"
                              ? slot.end_time?.slice(0, 5)
                              : slot.close_time?.slice(0, 5);
                          const isAvailable = slot.status === "Available";
                          return (
                            <button
                              key={index}
                              type="button"
                              className={`trial-card small slot-button ${selectedTrialTime === `${startTime} - ${endTime}` ? "active" : ""
                                } ${isAvailable ? "available" : "disabled"}`}
                              onClick={() =>
                                handleTrialTimeChange(
                                  `${startTime} - ${endTime}`,
                                  slot.batch_id,
                                  slot.status,
                                  slot.court_id
                                )
                              }
                              disabled={!isAvailable}
                            >
                              <Clock size={16} className="slot-icon" />
                              <span className="slot-time">{formatTo12Hour(startTime)} - {formatTo12Hour(endTime)}</span>
                            </button>

                          );
                        })
                      ) : (
                        <p className="no-time-slot">No Time Slots Available</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {currentStep === 4 && (
            <>
              <h1 className='h1lines'>Contact Information
              </h1>
              <div className="trial-section">
                <label className="trial-section-label">Contact Number</label>
                <div className="trial-input-row">
                  <Phone size={18} className="mr-2 text-gray-500" />
                  <input

                    className="trial-input"
                    type="tel"
                    value={contactNumber}
                    onChange={(e) => handleInputChange(e, "contactNumber")}
                    onKeyPress={(e) => !/^[0-9]$/.test(e.key) && e.preventDefault()}
                    maxLength={10}
                    placeholder="Enter 10-digit phone number"
                  />

                  {!showOtpInput && !isOtpVerified && (
                    <button
                      type="button"
                      onClick={handleSendOtpClick}
                      className="trial-btn-secondary"
                      disabled={isOtpLoading || isOtpSent}
                    >
                      {isOtpLoading ? "Sending..." : isOtpSent ? "OTP Sent" : "Send OTP"}
                    </button>
                  )}
                  {isOtpVerified && <CheckCircle2 className="text-green-600" />}
                </div>
              </div>

              {/* OTP */}
              {showOtpInput && !isOtpVerified && (
                <div className="trial-section">
                  <label className="trial-section-label">Verify OTP</label>
                  <div className="trial-input-row">
                    <input
                      className="trial-input"
                      type="text"
                      value={otp}
                      onChange={(e) => handleInputChange(e, "otp")}
                      maxLength={6}
                      placeholder="Enter OTP"
                    />
                    <button
                      type="button"
                      onClick={handleOtpVerification}
                      className="trial-btn-secondary"
                    >
                      Verify
                    </button>
                  </div>
                </div>
              )}

              {/* Name */}
              {isNewUser && isOtpVerified && (
                <div className="trial-section">
                  <label className="trial-section-label">Full Name</label>
                  <input
                    className="trial-input"
                    type="text"
                    value={userName}
                    onChange={(e) => handleInputChange(e, "userName")}
                    placeholder="Enter your name"
                  />
                </div>
              )}

              {/* Child Selection */}
              {isOtpVerified && !isNewUser && userDetails && (
                <div className="trial-section">
                  <label className="trial-section-label">Select User</label>

                  {selectedType === "playing" && selectedSport === "swimming" ? (
                    <>
                      {userDetails.parent ? (
                        <button
                          type="button"
                          className={`trial-card ${selectedChildId === "parent" ? "active" : ""}`}
                          onClick={() =>
                            handleChildSelect({ isParent: true, name: userDetails.parent.name })
                          }
                        >
                          {userDetails.parent.name}
                        </button>
                      ) : (
                        <div className="trial-input-row">
                          <input
                            className="trial-input"
                            type="text"
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                            placeholder="Enter your name"
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    // 🏸 Coaching case → filter invalid parent/children
                    <>
                      <div className="trial-card-options small">
                        {/* ✅ Show parent only if name exists */}
                        {userDetails.parent && userDetails.parent.name && userCategory === "Adult" && (
                          <button
                            type="button"
                            className={`trial-card ${selectedChildId === "parent" ? "active" : ""}`}
                            onClick={() =>
                              handleChildSelect({ isParent: true, name: userDetails.parent.name })
                            }
                          >
                            {userDetails.parent.name}
                          </button>
                        )}

                        {/* ✅ Filter children with valid id + name */}
                        {userDetails.children
                          .filter((child) => child?.name && (child?.id || child.id === null))
                          .map((child) => (
                            <button
                              key={child.id}
                              type="button"
                              className={`trial-card ${selectedChildId === child.id ? "active" : ""}`}
                              onClick={() => handleChildSelect(child)}
                            >
                              {child.name}
                            </button>
                          ))}
                      </div>

                      {/* Add user flow stays the same */}
                      {!showCreateChildInput ? (
                        <button
                          type="button"
                          className="trial-btn-secondary"
                          onClick={() => setShowCreateChildInput(true)}
                        >
                          <Plus size={16} className="mr-1" />
                          Add User
                        </button>
                      ) : (
                        <div className="trial-input-row">
                          <input
                            className="trial-input"
                            type="text"
                            value={newChildName}
                            onChange={(e) => setNewChildName(e.target.value)}
                            placeholder="Enter user's name"
                          />
                          <button
                            type="button"
                            className="trial-btn-secondary"
                            onClick={() => {
                              if (newChildName.trim()) {
                                const newChild = { id: null, name: newChildName };
                                setUserDetails((prev) => ({
                                  ...prev,
                                  children: [...prev.children, newChild],
                                }));
                                setUserName(newChildName);
                                setSelectedChildId(null);
                                setShowCreateChildInput(false);
                              }
                            }}
                          >
                            Save
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}

          <div className="nav-buttons">
            <button
              type="button"
              onClick={() => {
                if (currentStep === 3 && selectedType === 'playing' && selectedSport === 'swimming') {
                  setCurrentStep(1);
                } else {
                  setCurrentStep((prev) => Math.max(1, prev - 1));
                }
              }}
              disabled={currentStep === 1}
              className="btn-secondary"
            >
              <ChevronLeft size={16} className="mr-1" />
              Previous
            </button>

            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault(); // 👈 block accidental form submit
                  if (currentStep === 1 && selectedType === 'playing' && selectedSport === 'swimming') {
                    // Skip step 2 for swimming playing
                    setCurrentStep(3);
                  } else {
                    setCurrentStep((prev) => prev + 1);
                  }
                }}
                disabled={!canProceedToNextStep()}
                className="btn-primary"
              >
                Continue <ChevronRight size={16} className="ml-1" />
              </button>
            ) : (
              <>
                <div className="terms-checkbox-wrapper">
                  <label className="terms-checkbox-label">
                    <input
                      type="checkbox"
                      checked={isTermsAccepted}
                      onChange={(e) => {
                        setIsTermsAccepted(e.target.checked);
                      }}
                    />
                    <span>
                      I agree to{" "}
                      <button
                        type="button"
                        className="terms-link-btn"
                        onClick={() => setShowTermsModal(true)}
                      >
                        Terms & Conditions
                      </button>
                    </span>
                  </label>
                </div>
                <button type="submit" className="btn-success">
                  Proceed <CheckCircle2 size={16} className="ml-1" />
                </button>
              </>
            )}
          </div>
        </form>
      </div>
      <ConfirmationModal
        showModal={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        bookingData={formData}
      />
      <TermsModal
        showModal={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
      <button
        type="button"
        className="terms-link-btn"
        onClick={() => setShowTermsModal(true)}
      >
        Terms & Conditions
      </button>
    </div>
  );
};

export default BookTrial;
